import { motion } from "framer-motion";
import { FileText, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const sections = [
  {
    title: "1. Accettazione delle condizioni",
    body: [
      "I presenti Termini di Servizio («Termini») regolano l'uso dell'applicazione FuffyFam e del sito fuffyfam.com (insieme, il «Servizio»), offerti da FuffyFam («noi», «il Titolare»).",
      "Registrandoti all'app o utilizzando il Servizio dichiari di aver letto, compreso e accettato integralmente questi Termini e la nostra Privacy Policy. Se non accetti, ti invitiamo a non utilizzare il Servizio. Devi avere almeno 16 anni per creare un account.",
    ],
  },
  {
    title: "2. Descrizione del servizio",
    body: [
      "FuffyFam è una community digitale per proprietari di cani che permette di:",
    ],
    list: [
      "Gestire la salute del proprio cane: libretto sanitario digitale, vaccinazioni, peso e promemoria.",
      "Trovare e contattare dog sitter e strutture ricettive dog-friendly.",
      "Entrare in contatto con altri proprietari e cani nella propria zona.",
    ],
    footer: [
      "Il Servizio è fornito «così com'è»: alcune funzionalità possono essere in fase di lancio o evolvere nel tempo. Ci riserviamo di modificare, sospendere o interrompere funzioni con preavviso ragionevole.",
    ],
  },
  {
    title: "3. Account utente e responsabilità",
    list: [
      "Sei responsabile della riservatezza delle tue credenziali e di ogni attività svolta tramite il tuo account.",
      "Ti impegni a fornire informazioni veritiere e aggiornate su di te e sul tuo cane.",
      "È vietato usare il Servizio per scopi illeciti, molestie, spam, o per creare profili falsi o ingannevoli.",
      "Le informazioni sanitarie inserite (vaccini, peso, terapie) sono uno strumento organizzativo e non sostituiscono in alcun modo il parere del veterinario.",
      "Quando contatti sitter o altri utenti, sei l'unico responsabile degli accordi presi e degli incontri organizzati.",
    ],
  },
  {
    title: "4. Contenuti generati dagli utenti",
    body: [
      "Puoi pubblicare foto, testi, recensioni e informazioni («Contenuti»). Resti titolare dei tuoi Contenuti, ma concedi a FuffyFam una licenza non esclusiva, gratuita e mondiale per ospitarli, mostrarli e riprodurli all'interno del Servizio.",
    ],
    list: [
      "Non pubblicare contenuti illeciti, offensivi, violenti o che violino diritti di terzi (privacy, diritto d'autore).",
      "Le recensioni devono riflettere esperienze reali e non essere frutto di incentivi non dichiarati.",
      "Possiamo rimuovere senza preavviso Contenuti che violino questi Termini e sospendere gli account responsabili.",
      "Puoi cancellare i tuoi Contenuti in qualsiasi momento; la cancellazione dell'account li rimuove dal Servizio.",
    ],
  },
  {
    title: "5. Servizi di terze parti e partner",
    body: [
      "Dog sitter, strutture ricettive e negozi presenti nell'app sono soggetti indipendenti. FuffyFam agisce come piattaforma di intermediazione e non è parte dei contratti tra te e i partner.",
      "Qualsiasi accordo, pagamento o servizio concordato con un partner avviene sotto la tua esclusiva responsabilità. Ti invitiamo a verificare sempre recensioni e referenze.",
    ],
  },
  {
    title: "6. Proprietà intellettuale",
    body: [
      "Il marchio FuffyFam, il logo, il design e il codice del Servizio sono di nostra proprietà esclusiva. Non è consentito copiarli, riprodurli o utilizzarli senza autorizzazione scritta.",
    ],
  },
  {
    title: "7. Limitazione di responsabilità",
    list: [
      "Non rispondiamo di danni derivanti da incontri, accordi o servizi tra utenti o con partner terzi.",
      "Non garantiamo l'accuratezza delle informazioni inserite dagli utenti né la disponibilità ininterrotta del Servizio.",
      "Le informazioni sanitarie presenti nell'app non costituiscono consulenza veterinaria: per qualsiasi dubbio sulla salute del tuo cane rivolgiti sempre a un veterinario.",
      "Nei limiti consentiti dalla legge, la nostra responsabilità complessiva è limitata all'importo eventualmente pagato per il Servizio nei 12 mesi precedenti all'evento.",
      "Nulla in questi Termini limita i diritti irrinunciabili riconosciuti ai consumatori dal Codice del Consumo (D.Lgs. 206/2005).",
    ],
  },
  {
    title: "8. Sospensione e chiusura dell'account",
    body: [
      "Puoi chiudere il tuo account in qualsiasi momento dall'app (Profilo → Impostazioni → Elimina account) o scrivendoci.",
      "Possiamo sospendere o chiudere account che violino questi Termini, con preavviso salvo violazioni gravi.",
    ],
  },
  {
    title: "9. Legge applicabile e foro competente",
    body: [
      "I presenti Termini sono regolati dalla legge italiana. Per ogni controversia è competente il foro del consumatore, ai sensi dell'art. 66-bis del Codice del Consumo; per gli utenti non consumatori, il Foro di Milano.",
      "Prima di adire le vie legali, ti invitiamo a contattarci per cercare una soluzione amichevole. Puoi inoltre ricorrere alla piattaforma europea di risoluzione delle controversie online (ODR).",
    ],
  },
  {
    title: "10. Modifiche ai Termini",
    body: [
      "Potremmo aggiornare questi Termini per adeguarli all'evoluzione del Servizio o della normativa. Le modifiche sostanziali ti saranno comunicate tramite l'app o via email con ragionevole preavviso. L'uso continuato del Servizio dopo la modifica costituisce accettazione. Ultimo aggiornamento: settembre 2026.",
    ],
  },
  {
    title: "11. Contatti e assistenza",
    body: [
      "Per assistenza sul Servizio, domande su questi Termini o segnalazioni, scrivici a supporto.fuffyfam@gmail.com. Per questioni di privacy: supporto.fuffyfam@gmail.com.",
    ],
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1A1A]" data-testid="terms-page">
      <nav className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6 border-b border-[#1A1A1A]/[0.06]">
        <Logo testId="terms-logo" />
        <Link
          to="/"
          data-testid="terms-back-link"
          className="flex items-center gap-2 text-sm font-semibold text-[#5C5C5C] hover:text-[#FF6B35] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>
      </nav>

      <main className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B35] mb-6">
            <FileText className="w-4 h-4" />
            Documento legale
          </p>
          <h1
            data-testid="terms-title"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
          >
            Termini di Servizio
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            Le regole del gioco, scritte in chiaro: cosa puoi aspettarti da FuffyFam e cosa
            chiediamo a te. Ultimo aggiornamento: settembre 2026.
          </p>
        </motion.div>

        <div className="mt-14 space-y-12">
          {sections.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.05 * (i % 3), ease: [0.16, 1, 0.3, 1] }}
              data-testid={`terms-section-${i + 1}`}
            >
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
                {s.title}
              </h2>
              {s.body?.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-[#5C5C5C]">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-3">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex gap-3 text-base leading-relaxed text-[#5C5C5C]">
                      <span className="mt-[0.6rem] w-1.5 h-1.5 rounded-full bg-[#FF6B35] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {s.footer?.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-[#5C5C5C]">
                  {p}
                </p>
              ))}
            </motion.section>
          ))}
        </div>
      </main>

      <footer className="border-t border-[#1A1A1A]/[0.08] px-6 sm:px-10 lg:px-16 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl mx-auto">
        <p className="text-sm text-[#5C5C5C]">© 2026 FuffyFam · fuffyfam.com</p>
        <div className="flex items-center gap-5">
          <Link
            to="/privacy"
            data-testid="terms-privacy-link"
            className="text-sm font-semibold text-[#5C5C5C] hover:text-[#FF6B35] transition-colors duration-300"
          >
            Privacy Policy
          </Link>
          <a
            href="mailto:supporto.fuffyfam@gmail.com"
            data-testid="terms-contact-link"
            className="text-sm font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors duration-300"
          >
            supporto.fuffyfam@gmail.com
          </a>
        </div>
      </footer>
    </div>
  );
}
